import { useEffect, RefObject } from "react";

/**
 * Custom hook to ensure videos autoplay properly across all browsers
 * This handles Safari's strict autoplay policy and low power mode
 */
export const useAutoplayVideo = (
  videoRef: RefObject<HTMLVideoElement>,
  options = { muted: true, playbackRate: 1 }
): void => {
  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    // Always mute videos to ensure autoplay works
    video.muted = options.muted;

    // Set playback rate
    video.playbackRate = options.playbackRate;

    // Set up attributes for better autoplay
    video.setAttribute("playsinline", "");
    video.setAttribute("preload", "auto");

    // Function to attempt playback with error handling
    const attemptPlay = async () => {
      try {
        if (video.paused) {
          await video.play();
          console.log("Video playback started successfully");
        }
      } catch (error) {
        console.warn("Autoplay was prevented:", error);

        // Fall back to showing poster image
        // Video will be hidden by our opacity: 0 CSS
      }
    };

    // Try to play when metadata is loaded
    const onLoadedMetadata = () => {
      attemptPlay();
    };

    // Try to play when enough data is buffered to start playback
    const onCanPlay = () => {
      attemptPlay();
    };

    // Try again if paused
    const onPause = () => {
      // Only attempt to restart if video is in viewport
      const rect = video.getBoundingClientRect();
      const isVisible =
        rect.top >= -rect.height &&
        rect.bottom <= window.innerHeight + rect.height;

      if (isVisible) {
        attemptPlay();
      }
    };

    // Add event listeners
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("canplay", onCanPlay);
    video.addEventListener("pause", onPause);

    // Handle visibility change (tab switches, etc)
    document.addEventListener("visibilitychange", attemptPlay);

    // Clean up
    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("pause", onPause);
      document.removeEventListener("visibilitychange", attemptPlay);
    };
  }, [videoRef, options.muted, options.playbackRate]);
};
